import { consumeReportReturnPath } from '@src/packages/dashboard/utils/reportUrl';
import React, { useEffect } from 'react';

import URLSearchParams from 'url-search-params';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';

import { get, remove } from 'packages/utils/storage';
import asyncComponent from 'util/asyncComponent';
import { BACKOFFICE_BASE_PATH } from 'packages/backoffice';
import {
  AcceptInvitationScreen,
  AcceptInvitationV2Screen,
  CompleteRegistrationScreen,
} from 'packages/users';
import { isPackedClaims } from 'packages/utils/access';
import { isPostDeleteOrganizationSetupSearch } from '@src/containers/App/Onboarding/onboardingSetup';
import {
  COMPLETE_REGISTRATION_PATH,
  takeRememberedCompleteRegistration,
} from '@src/packages/users/utils/completeRegistrationPath';
import {
  mustCompleteRegistration,
  resolveRegistrationRedirect,
} from '@src/containers/App/Onboarding/mustCompleteRegistration';
import {
  authTokenListenerRegister,
  onLayoutTypeChange,
  onNavStyleChange,
  setInitUrl,
  setThemeType,
} from 'appRedux/actions';
import {
  LAYOUT_TYPE_BOXED,
  LAYOUT_TYPE_FRAMED,
  LAYOUT_TYPE_FULL,
  NAV_STYLE_ABOVE_HEADER,
  NAV_STYLE_BELOW_HEADER,
  NAV_STYLE_DARK_HORIZONTAL,
  NAV_STYLE_DEFAULT_HORIZONTAL,
  NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
} from 'constants/ThemeSetting';

import Auth from './Auth';
import { redirectToReportSignIn } from './reportSignInRedirect';
import MainApp from './MainApp';
import Error404 from './Error404';
import Error403 from './Error403';
import Onboarding from './Onboarding';
import { isLocationAlertPublicPathname } from './LocationAlert/locationAlert.utils';
import LocationAlert from './LocationAlert/screens/Alert';
import MainAlert from './LocationAlert/screens/MainAlert';
import Notifications from './Subscriptions/screens/Notification';
import CancelAttendanceEvent from './AttendanceEvent/screens/CancelAttendanceEvent';
import CancelMembershipChange from './WorkJourney/screens/CancelMembershipChange';

import 'packages/styles';
import ConfirmEmail from './ConfirmEmail';
import EmailChangeConfirm from './EmailChangeConfirm';
import { MarkingPortal } from './MarkingPortal';
import {
  buildLegacyCheckInRedirectLocation,
  getMarkingPortalToken,
  isLegacyCheckInPathname,
  isMarkingPortalPathname,
  isPublicPortalEntryPath,
  LEGACY_MARKING_PORTAL_CHECKIN_PATH,
  MARKING_PORTAL_PATH,
} from './MarkingPortal/markingPortal.utils';

const BackOfficeApp = asyncComponent(() => import('packages/backoffice/screens/BackOfficeApp'));

const INVITATION_REGEXP = /^\/invitation\/([a-z0-9=]{5,})\/?$/i;
const INVITATION_V2_REGEXP = /^\/invitationV2\/[^/]+\/[^/]+\/?$/;
const WORK_ADDRESS = 'dt';
const WORK_ADDRESS_REGEXP = new RegExp(`^/${WORK_ADDRESS}/?$`);
const LOCATION_ALERT_PATH_REGEXP = /^\/locationAlert(\/.*)?$/;

const anonymousPaths = [
  /^\/forgot_password\/?$/,
  /^\/signin\/?$/,
  /^\/signup\/?$/,
  /^\/auth_email_action\/?$/,
  /^\/email_link\/?$/,
  /^\/reset_password\/?$/,
  /^\/email-change\/confirm\/?$/,
  LOCATION_ALERT_PATH_REGEXP,
  WORK_ADDRESS_REGEXP,
  INVITATION_REGEXP,
  INVITATION_V2_REGEXP,
];

const OperondaApp = () => {
  const dispatch = useDispatch();
  const { navStyle, layoutType } = useSelector(({ settings }) => settings);

  const { authUser, initURL } = useSelector(({ auth }) => auth);
  const [loaded, onboardingCompleted, profile] = useSelector(({ user }) => [
    user.profile.loaded,
    user.profile.data?.onboardingCompleted,
    user.profile.data,
  ]);
  // If an user has organization is because the user was invited to an organization
  const userHasOrganizations = Object.keys(profile?.organizations || {}).length > 0;
  const claims = useSelector(({ user }) => user.access.data?.claims);
  const orgUser = useSelector(({ user }) => user.orgUser?.data);
  const orgUserLoaded = useSelector(({ user }) => user.orgUser?.loaded);
  const activeOrgId = useSelector(({ organizations }) => organizations.organization.id);
  // A packed token has no org role until the org user document arrives.
  const orgRolePending = isPackedClaims(claims) && orgUserLoaded === false;
  const shouldCompleteRegistration = mustCompleteRegistration({
    claims,
    orgId: activeOrgId,
    orgUser,
    onboardingCompleted,
  });

  const location = useLocation();
  const history = useHistory();
  const { pathname, search } = location;
  const markingPortalToken = getMarkingPortalToken(search);
  const isMarkingPortalPath = isMarkingPortalPathname(pathname);
  const isLegacyCheckInPath = isLegacyCheckInPathname(pathname);
  const isPublicPortalEntry = isPublicPortalEntryPath(pathname, search);
  const canAccessMarkingPortal =
    isPublicPortalEntry || (loaded && !!onboardingCompleted && authUser);
  const shouldFetchUser = !!authUser && !loaded;
  const publicDevice = get('publicDevice');
  const isPublicMarkingPortalPath = isMarkingPortalPath && markingPortalToken && authUser;

  /**
   * Register the auth token listener for the user or the marking portal token listener
   * for the public marking portal path. Skip on public location-alert URLs so a stale
   * local session cannot force sign-out / login redirect.
   */
  useEffect(() => {
    if (isLocationAlertPublicPathname(pathname)) {
      return;
    }
    if (shouldFetchUser || isPublicMarkingPortalPath) {
      dispatch(authTokenListenerRegister());
      if (publicDevice) {
        remove('user_id');
      }
    }
  }, []);

  useEffect(() => {
    if (initURL === '') {
      dispatch(setInitUrl(pathname));
    }
    const params = new URLSearchParams(search);

    if (params.has('theme')) {
      dispatch(setThemeType(params.get('theme')));
    }
    if (params.has('nav-style')) {
      dispatch(onNavStyleChange(params.get('nav-style')));
    }
    if (params.has('layout-type')) {
      dispatch(onLayoutTypeChange(params.get('layout-type')));
    }
    setLayoutType(layoutType);
    setNavStyle(navStyle);
  }, [dispatch, pathname, search, initURL, layoutType, navStyle]);

  const setLayoutType = type => {
    if (type === LAYOUT_TYPE_FULL) {
      document.body.classList.remove('boxed-layout');
      document.body.classList.remove('framed-layout');
      document.body.classList.add('full-layout');
    } else if (type === LAYOUT_TYPE_BOXED) {
      document.body.classList.remove('full-layout');
      document.body.classList.remove('framed-layout');
      document.body.classList.add('boxed-layout');
    } else if (type === LAYOUT_TYPE_FRAMED) {
      document.body.classList.remove('boxed-layout');
      document.body.classList.remove('full-layout');
      document.body.classList.add('framed-layout');
    }
  };

  const setNavStyle = style => {
    if (
      style === NAV_STYLE_DEFAULT_HORIZONTAL ||
      style === NAV_STYLE_DARK_HORIZONTAL ||
      style === NAV_STYLE_INSIDE_HEADER_HORIZONTAL ||
      style === NAV_STYLE_ABOVE_HEADER ||
      style === NAV_STYLE_BELOW_HEADER
    ) {
      document.body.classList.add('full-scroll');
      document.body.classList.add('horizontal-layout');
    } else {
      document.body.classList.remove('full-scroll');
      document.body.classList.remove('horizontal-layout');
    }
  };

  useEffect(() => {
    const isAnonymousPath = anonymousPaths.some(ap => pathname.match(ap));
    const isInvitationPath =
      pathname.match(INVITATION_REGEXP) || pathname.match(INVITATION_V2_REGEXP);
    const isOnboardingPath = pathname.match(/^\/onboarding(|\/wizard)\/?$/);
    const isPostDeleteOrganizationSetup =
      pathname.match(/^\/onboarding\/wizard\/?$/) &&
      isPostDeleteOrganizationSetupSearch(search);
    const isSubscriptionPath = pathname.match(/^\/subscriptions(\/.*)?$/);
    const isLocationAlertPath = isLocationAlertPublicPathname(pathname);
    const isConfirmEmailPath = pathname.match(/^\/shouldConfirmEmail\/?$/);
    const isEmailChangeConfirmPath = pathname.match(/^\/email-change\/confirm\/?$/);
    const isAttendanceEventCancelPath = pathname.match(/^\/attendance-event\/cancel\/?$/);
    const isPublicMarkingPortalPath = isPublicPortalEntry;

    //NOTE: Here we are going to exclude the soem paths since they are public paths
    if (
      isSubscriptionPath ||
      isLocationAlertPath ||
      isEmailChangeConfirmPath ||
      isAttendanceEventCancelPath ||
      isPublicMarkingPortalPath
    ) {
      return;
    }

    if (!shouldFetchUser) {
      // We don't have a clue about the user

      if (loaded) {
        // An existing account that signed in to accept an invite goes to that form once.
        const rememberedPath = !isInvitationPath && takeRememberedCompleteRegistration();
        if (rememberedPath) {
          history.push(rememberedPath);
          return;
        }
        const registration = resolveRegistrationRedirect({
          shouldCompleteRegistration,
          onboardingCompleted,
          userHasOrganizations,
          orgRolePending,
          activeOrgId,
          orgUserLoaded,
          pathname,
          search,
          isInvitationPath,
        });
        if (registration) {
          if (registration.to) {
            history.push(registration.to);
          }
          return;
        }
        if (!onboardingCompleted) {
          // Check if user needs email confirmation (invited users)
          if (
            !isInvitationPath &&
            ((userHasOrganizations && onboardingCompleted === undefined) ||
              onboardingCompleted === null)
          ) {
            if (!isConfirmEmailPath) {
              history.push('/shouldConfirmEmail');
            }
            return; // Exit early to prevent further navigation
          }

          // We've loaded the user but is not onboarded
          if (!isOnboardingPath && !isInvitationPath) {
            history.push('/onboarding');
          }
        } else if (!isInvitationPath && (isAnonymousPath || isOnboardingPath)) {
          if (isPostDeleteOrganizationSetup) {
            return;
          }
          // We've loaded the user but this is a public path
          history.push(consumeReportReturnPath('/'));
        }
      } else if (!isAnonymousPath) {
        // This is a restricted path
        redirectToReportSignIn({ pathname, search }, history);
      }
    }
  }, [
    dispatch,
    history,
    loaded,
    onboardingCompleted,
    pathname,
    search,
    shouldFetchUser,
    userHasOrganizations,
    shouldCompleteRegistration,
    orgRolePending,
    activeOrgId,
    orgUserLoaded,
    markingPortalToken,
    isMarkingPortalPath,
    isLegacyCheckInPath,
  ]);

  return (
    <Switch>
      <Route component={AcceptInvitationScreen} exact path="/invitation/:token" />
      <Route
        component={AcceptInvitationV2Screen}
        exact
        path="/invitationV2/:organizationId/:userId"
      />
      <Route component={Error403} exact path="/error403" />
      <Route component={Error404} exact path="/error404" />
      <Route component={Notifications} exact path="/subscriptions/notifications/:type" />
      <Route component={Notifications} exact path="/subscriptions/notifications/:type/:token" />
      <Route component={CancelAttendanceEvent} exact path="/attendance-event/cancel" />
      <Route
        component={CancelMembershipChange}
        exact
        path="/work-journey/membership-change/cancel"
      />
      <Route component={MainAlert} exact path="/locationAlert/:orgId/div/:divId/:locationId" />
      <Route component={LocationAlert} exact path="/locationAlert" />
      <Route component={EmailChangeConfirm} exact path="/email-change/confirm" />

      <Route
        exact
        path={LEGACY_MARKING_PORTAL_CHECKIN_PATH}
        render={() => <Redirect push={false} to={buildLegacyCheckInRedirectLocation(search)} />}
      />

      {canAccessMarkingPortal && (
        <Route component={MarkingPortal} exact path={MARKING_PORTAL_PATH} />
      )}

      {loaded && (
        <Route component={CompleteRegistrationScreen} exact path={COMPLETE_REGISTRATION_PATH} />
      )}

      {!loaded && <Route component={Auth} path="/" />}
      {/* Platform staff back office: authenticated, but outside any organization. */}
      {!shouldFetchUser && !!onboardingCompleted && (
        <Route component={BackOfficeApp} path={BACKOFFICE_BASE_PATH} />
      )}
      {!shouldFetchUser &&
        (!!onboardingCompleted ? (
          <Route path="/:orgId?/-" component={MainApp} />
        ) : userHasOrganizations &&
          (onboardingCompleted === undefined || onboardingCompleted === null) ? (
          <Route component={ConfirmEmail} exact path="/shouldConfirmEmail" />
        ) : (
          <Route component={Onboarding} path="/onboarding" />
        ))}

      <Redirect exact from="/" to="/-" />
      <Redirect to="/error404" />
    </Switch>
  );
};

export default OperondaApp;
